import { Box, Paper, Stack, Typography } from '@mui/material';

import { maxCount, percentOf } from './insightMath';
import type { FrequencyInsight } from './types';

export type FrequencyInsightCardProps = {
  insight: FrequencyInsight;
};

// Yes/no and tag-style observations render as frequency bars (never a pie) —
// each item is an independent count out of the visit total (SFOA-35 AC #3).
// Bar length is scaled to the most common item so the relative frequencies are
// legible; the trailing label always shows the absolute "N / total (P%)".
export const FrequencyInsightCard = ({ insight }: FrequencyInsightCardProps) => {
  const { title, subtitle, items, visitCount } = insight;
  const peak = maxCount(items);

  return (
    <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2.5 }, minWidth: 0 }}>
      <Stack sx={{ mb: 2, gap: 0.25 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, overflowWrap: 'break-word' }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Stack>
      <Stack sx={{ gap: 1.5 }}>
        {items.map(item => {
          const percent = percentOf(item.count, visitCount);
          const fill = peak > 0 ? (Math.max(item.count, 0) / peak) * 100 : 0;
          return (
            <Stack
              key={item.label}
              direction="row"
              sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 0.5, columnGap: 2 }}
            >
              {/* No fixed width + no noWrap: a long label wraps onto a second
                  line instead of being clipped or forcing the row wider than
                  the card. flex: 0 1 auto lets it shrink down to that wrap
                  point rather than pushing the bar off to the side. */}
              <Typography variant="body2" sx={{ flex: '0 1 180px', minWidth: 0, overflowWrap: 'break-word' }}>
                {item.label}
              </Typography>
              <Stack
                direction="row"
                sx={{ alignItems: 'center', gap: 2, flex: '1 1 220px', minWidth: 0 }}
              >
                <Box
                  role="meter"
                  aria-label={item.label}
                  aria-valuenow={item.count}
                  aria-valuemin={0}
                  aria-valuemax={visitCount}
                  sx={{ flexGrow: 1, minWidth: 0, height: 10, borderRadius: 5, bgcolor: 'grey.200', overflow: 'hidden' }}
                >
                  <Box sx={{ width: `${fill}%`, height: '100%', borderRadius: 5, bgcolor: 'primary.main' }} />
                </Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    flexShrink: 0,
                    textAlign: 'right',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {item.count} / {visitCount} ({percent}%)
                </Typography>
              </Stack>
            </Stack>
          );
        })}
      </Stack>
    </Paper>
  );
};
